import { useState } from 'react';
import {
  Button,
  GlassCard,
  HelpAnchor,
  Pill,
  SkeletonText,
  SurfacePageSectionHeader,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { currencyGlyph } from '@/versions/v1/shared/money';
import { ControlChart, KpiCard, MeterBar, type VizTone } from '@/versions/v1/modules/_viz';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import {
  WARM_UP_HATCH,
  dataQualityRead,
  diagnosisOrderedCauses,
  evaluabilityLabel,
  evaluabilityTone,
  excursionRows,
  caseClockLabel,
  expectationRows,
  financialRead,
  firedSignals,
  knowledgeRead,
  modeBand,
  propagationRows,
  provenanceText,
  reasonLabel,
  RUN_ESTIMATE_LABEL,
  signalSummary,
  sentenceTarpInstruction,
  valuePathRows,
  windowRead,
  type ExcursionRow,
  type FiredSignalRow,
} from '@/versions/v1/modules/advisory/AnalysisRun.transform';
import type { AnalysisRunState } from '@/versions/v1/shared/useAnalysisRun';
import type { AnalysisReplayController } from '@/versions/v1/shared/useAnalysisReplay';
import {
  analysisReplaySourceLabel,
  analysisResultChartProps,
} from '@/versions/v1/modules/advisory/AnalysisReplay.transform';

function excursionTone(row: ExcursionRow): 'default' | 'danger' | 'warning' {
  if (row.state === 'active') return 'danger';
  if (row.state === 'suppressed') return 'warning';
  return 'default';
}

function excursionText(row: ExcursionRow): string {
  if (row.heldMinutes > 0) {
    return `${row.ruleLabel} · ${row.state} · held ${row.heldLabel}${row.heldAggregate ? ' aggregate' : ''}`;
  }
  if (row.direction) return `${row.ruleLabel} · ${row.state} · ${row.direction}`;
  return `${row.ruleLabel} · ${row.state}`;
}

function confidenceTone(confidencePct: number): VizTone {
  if (confidencePct >= 80) return 'ok';
  if (confidencePct >= 50) return 'warn';
  return 'bad';
}

/**
 * The window the evidence came from and the excursions it found, as facts
 * beside the case's own chips.
 *
 * The run is fetched once per open by the drawer and handed down, so whether
 * this block is on the glass never changes how many hooks ran.
 */
export function AnalysisRunFacts({ run }: { run: AnalysisRunState }) {
  if (run.status === 'loading') return <SkeletonText lines={2} />;
  if (run.status !== 'ready') return null;

  const evidenceWindow = windowRead(run.result);
  const excursions = excursionRows(run.result);

  return (
    <div className="space-y-1">
      <p className="text-sm text-muted">{evidenceWindow.label}</p>
      <div className="flex flex-wrap gap-1">
        {excursions.map((row) => (
          <Pill
            key={row.id}
            tone={excursionTone(row)}
            className="normal-case"
            title={`${row.id} · ${row.firstAt} → ${row.lastAt}`}
          >
            {excursionText(row)}
          </Pill>
        ))}
      </div>
    </div>
  );
}

/** The service's own verdict on the window: how well, and on which tags. */
export function AnalysisRunQuality({ run }: { run: AnalysisRunState }) {
  if (run.status !== 'ready') return null;

  const quality = dataQualityRead(run.result, run.slices);
  const reason = reasonLabel(quality.reason);

  return (
    <Commentable name="Drawer.AnalysisRun">
      <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
        <SurfacePageSectionHeader
          title="Data quality"
          actions={<HelpAnchor doc="advisory/analysis-run" />}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={evaluabilityTone(quality.evaluability)}>
            {evaluabilityLabel(quality.evaluability)}
          </Pill>
          {reason ? <Pill tone="default">{reason}</Pill> : null}
        </div>
        {quality.confidencePct === null ? null : (
          <MeterBar
            value={quality.confidencePct}
            max={100}
            tone={confidenceTone(quality.confidencePct)}
            label="Confidence"
            valueLabel={`${quality.confidencePct} %`}
          />
        )}
        <Table wrap={false} className="text-xs">
          <THead>
            <TR>
              <TH className="w-[40%] px-0 py-1">Source</TH>
              <TH className="px-0 py-1 text-right">Missing</TH>
              <TH className="px-0 py-1 text-right">Bad</TH>
              <TH className="px-0 py-1 text-right">Warm-up</TH>
            </TR>
          </THead>
          <TBody>
            {quality.roles.map((row) => (
              <TR key={row.signalRole}>
                <TD className="px-0 py-1" title={row.signalRole}>
                  {row.sourceTag}
                </TD>
                <TD className="px-0 py-1 text-right tabular-nums">{row.missing}</TD>
                <TD className="px-0 py-1 text-right tabular-nums">{row.bad}</TD>
                <TD className="px-0 py-1 text-right tabular-nums">{row.warmUp}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </section>
    </Commentable>
  );
}

/**
 * The window as one band of segments — what the circuit was doing, for how
 * long, and which of it was warm-up. The band is layout: the segment widths
 * are the only thing computed, and the mode at close and the service's own
 * uncertainty read as two pills beside the legend.
 */
export function AnalysisRunMode({ run }: { run: AnalysisRunState }) {
  if (run.status !== 'ready') return null;

  const band = modeBand(run.result);

  return (
    <Commentable name="Drawer.AnalysisRunMode">
      <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
        <SurfacePageSectionHeader
          title="Operating mode"
          actions={<HelpAnchor doc="advisory/analysis-run" section="operating-mode" />}
        />
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--ink-tertiary)_18%,transparent)]">
          {band.segments.map((segment, index) => (
            <div
              key={`${segment.start}-${index}`}
              className="h-full"
              style={{
                width: `${segment.widthPct}%`,
                backgroundColor: segment.fill,
                backgroundImage: segment.warmUp ? WARM_UP_HATCH : undefined,
              }}
              title={`${segment.label} · ${segment.startClock} → ${segment.endClock}${
                segment.warmUp ? ' · warm-up' : ''
              }`}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
          {band.legend.map((entry) => (
            <span key={entry.mode} className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: entry.fill }}
              />
              {entry.label}
            </span>
          ))}
          <div className="ml-auto flex gap-1">
            <Pill tone="default">{`${band.modeAtEndLabel} at close`}</Pill>
            {band.uncertain ? <Pill tone="warning">Mode uncertain</Pill> : null}
          </div>
        </div>
      </section>
    </Commentable>
  );
}

/* -------------------------------------------------------------------------
   Signal — the evaluations that fired, and the expectation beside them.
   ------------------------------------------------------------------------- */

/** A signal that neither held nor trended has nothing to say in the Held cell. */
function heldCell(row: FiredSignalRow): string {
  if (row.heldMinutes > 0) return row.heldLabel;
  if (row.direction) return row.direction;
  return '';
}

function shortfallTone(tone: VizTone): 'danger' | 'warning' | 'success' {
  if (tone === 'bad') return 'danger';
  if (tone === 'warn') return 'warning';
  return 'success';
}

type ReadyReplayController = Extract<AnalysisReplayController, { status: 'ready' }>;

function ReplayControls({ replay }: { replay: ReadyReplayController }) {
  const frameTime = caseClockLabel(replay.frame.at);

  return (
    <div className="space-y-2 rounded-lg border border-[var(--hairline)] bg-[var(--surface-2)] px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" className="h-8 px-2 text-xs" onClick={replay.togglePlayback}>
          {replay.playing ? 'Pause' : 'Play'}
        </Button>
        <div className="flex items-center gap-1" aria-label="Replay speed">
          {([1, 4, 16] as const).map((speed) => (
            <Button
              key={speed}
              variant="ghost"
              className={`h-8 px-2 text-xs ${
                replay.framesPerSecond === speed ? 'text-accent-700 dark:text-accent-400' : 'text-muted'
              }`}
              aria-pressed={replay.framesPerSecond === speed}
              title={`${speed} frame${speed === 1 ? '' : 's'} per second`}
              onClick={() => replay.setFramesPerSecond(speed)}
            >
              {speed}×
            </Button>
          ))}
        </div>
        <span className="shrink-0">
          <ProvenanceChip text={analysisReplaySourceLabel(replay.source)} />
        </span>
        <time className="ml-auto text-xs tabular-nums text-muted" dateTime={replay.frame.at}>
          {frameTime}
        </time>
      </div>
      <input
        type="range"
        min={0}
        max={replay.frames.length - 1}
        step={1}
        value={replay.frameIndex}
        aria-label="Replay frame time"
        className="w-full accent-[var(--accent)]"
        onChange={(event) => replay.selectFrame(Number(event.currentTarget.value))}
      />
    </div>
  );
}

/**
 * The evaluations the service fired, the reasons the rest did not, and what the
 * loss is expected to cost in recovery.
 *
 * The fired fact differs by rule, so the chart does too: a breach is drawn
 * against the limits it crossed, a run on its own points because the descent is
 * what fired. Only the first fired entry of each rule is charted — the service
 * re-baselines per mode run, and two segments' limits are not one series.
 */
export function AnalysisRunSignal({
  run,
  replay,
}: {
  run: AnalysisRunState;
  replay?: AnalysisReplayController;
}) {
  const [open, setOpen] = useState(false);
  if (run.status !== 'ready') return null;

  const fired = firedSignals(run.result, run.slices);
  const chartProps = analysisResultChartProps(run.result, run.slices);
  const summary = signalSummary(run.result);
  const expectations = expectationRows(run.result, run.slices);
  const replayReady = replay?.status === 'ready' ? replay : null;
  if (fired.length === 0 && expectations.length === 0 && replayReady === null) return null;

  return (
    <Commentable name="Drawer.AnalysisRunSignal">
      <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
        <SurfacePageSectionHeader
          title="Signal"
          actions={<HelpAnchor doc="advisory/analysis-run" section="signal" />}
        />
        {replayReady ? <ReplayControls replay={replayReady} /> : null}
        {fired.length > 0 ? (
          <>
            <Table wrap={false} className="text-xs">
              <THead>
                <TR>
                  <TH className="px-0 py-1">Rule</TH>
                  <TH className="px-0 py-1">Mode</TH>
                  <TH className="px-0 py-1">Segment</TH>
                  <TH className="px-0 py-1">Held</TH>
                  <TH className="px-0 py-1 text-right">Points</TH>
                </TR>
              </THead>
              <TBody>
                {fired.map((row, index) => (
                  <TR key={`${row.ruleId}-${row.segmentStart}-${index}`} title={row.title}>
                    <TD className="px-0 py-1">{row.ruleLabel}</TD>
                    <TD className="px-0 py-1">{row.modeLabel}</TD>
                    <TD className="px-0 py-1 tabular-nums">
                      {`${row.segmentStartClock} → ${row.segmentEndClock}`}
                    </TD>
                    <TD className="px-0 py-1">{heldCell(row)}</TD>
                    <TD className="px-0 py-1 text-right tabular-nums">{row.pointsLabel}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            {chartProps.map((chart) => (
              <div key={chart.key} className="space-y-1">
                  <ControlChart model={chart.model} height={164} />
                  <p className="text-xs text-muted">{chart.caption}</p>
                </div>
            ))}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted">
                {`${summary.notFired} did not fire · ${summary.evaluatedClean} evaluated · ${summary.notEvaluated} not evaluated`}
              </p>
              {summary.notEvaluated > 0 ? (
                <button
                  type="button"
                  className="text-xs font-medium text-accent-700 hover:underline dark:text-accent-400"
                  onClick={() => setOpen((current) => !current)}
                >
                  {open ? 'Show fewer' : `Show ${summary.notEvaluated} not evaluated`}
                </button>
              ) : null}
            </div>
            {open && summary.notEvaluated > 0 ? (
              <Table wrap={false} className="text-xs">
                <TBody>
                  {summary.reasons.map((row) => (
                    <TR key={row.reason}>
                      <TD className="px-0 py-1">{row.label}</TD>
                      <TD className="px-0 py-1 text-right tabular-nums">{row.count}</TD>
                    </TR>
                  ))}
                  {summary.suppressedModes.map((row) => (
                    <TR key={row.mode}>
                      <TD className="px-0 py-1">{`${row.label} · suppressed`}</TD>
                      <TD className="px-0 py-1 text-right tabular-nums">{row.count}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            ) : null}
          </>
        ) : null}
        {expectations.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-ink-primary">Expected recovery</p>
            {expectations.map((row) => (
              <div key={row.signalRole} className="space-y-1.5" title={row.title}>
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="flex items-baseline gap-2">
                    <span className="font-medium text-ink-primary">{row.label}</span>
                    <Pill tone={shortfallTone(row.tone)} className="normal-case">
                      {row.shortfallLabel}
                    </Pill>
                  </span>
                  <span className="tabular-nums text-muted">{row.observedLabel}</span>
                </div>
                <MeterBar
                  value={row.observed * 100}
                  max={100}
                  band={{ low: row.bandLow * 100, high: row.bandHigh * 100 }}
                  marker={{ value: row.expected * 100, label: row.expectedLabel }}
                  tone={row.tone}
                />
              </div>
            ))}
          </div>
        ) : null}
      </section>
    </Commentable>
  );
}

/* -------------------------------------------------------------------------
   Process effect — how far the loss travels from the bank that lost it.
   ------------------------------------------------------------------------- */

/**
 * The deepest path the effect travelled, then every path behind it.
 *
 * The headline is the chain itself — node labels joined by arrows, taken from
 * the plant's own process graph — with the magnitude at its right. The rung
 * lists the paths the service inspected, indented by depth, so the reader
 * follows one effect through the circuit rather than reading eight rows of
 * equal weight. Depth is the indent and nothing else: no depth number, no
 * grouping, no second arrow chain inside the list.
 *
 * The rung opens onto the envelope's own paths; a service that inspected none
 * has nothing behind it, so the control is absent rather than counting zero.
 */
export function AnalysisRunPropagation({ run }: { run: AnalysisRunState }) {
  const [open, setOpen] = useState(false);
  if (run.status !== 'ready') return null;

  const read = propagationRows(run.result, run.slices);
  if (read === null) return null;

  const rungLabel = open
    ? 'Show fewer'
    : read.count === 1
      ? 'Show 1 path'
      : `Show ${read.count} paths`;

  return (
    <Commentable name="Drawer.AnalysisRunPropagation">
      <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
        <SurfacePageSectionHeader
          title="Process effect"
          actions={<HelpAnchor doc="advisory/analysis-run" section="process-effect" />}
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs">
            {read.headline.labels.join(' → ')}
            <span className="ml-2 tabular-nums text-muted">{read.headline.magnitudeLabel}</span>
          </p>
          {read.count > 0 ? (
            <button
              type="button"
              className="text-xs font-medium text-accent-700 hover:underline dark:text-accent-400"
              onClick={() => setOpen((current) => !current)}
            >
              {rungLabel}
            </button>
          ) : null}
        </div>
        {open && read.count > 0 ? (
          <div className="space-y-1">
            {read.rows.map((row) => (
              <div
                key={row.effect}
                className="flex items-baseline justify-between gap-2 text-xs"
                style={{ paddingLeft: `${row.depth * 12}px` }}
                title={row.title}
              >
                <span>
                  {row.toLabel}
                  {row.viaLabels.length ? (
                    <span className="text-muted">{` via ${row.viaLabels.join(', ')}`}</span>
                  ) : null}
                </span>
                <span className="tabular-nums">{row.magnitudeLabel}</span>
              </div>
            ))}
          </div>
        ) : null}
      </section>
    </Commentable>
  );
}

/* -------------------------------------------------------------------------
   Estimated EBITDA impact — what the condition is costing, and the three
   lines that make it up.
   ------------------------------------------------------------------------- */

/**
 * The two headline figures and the breakdown behind them.
 *
 * The section's own words are the envelope's `label` with its first letter
 * raised, so the service names the surface and the drawer never retypes it.
 * The pill decodes the basis and carries the formula revision on its title;
 * each component row carries its formula and unit the same way. The breakdown
 * sits on this rung rather than behind a disclosure because the two cards and
 * three rows are one read — five items, and the depth that stays down is the
 * per-row formula, which is a tooltip rather than a rung.
 */
export function AnalysisRunFinancial({ run }: { run: AnalysisRunState }) {
  const [open, setOpen] = useState(false);
  if (run.status !== 'ready') return null;

  const read = financialRead(run.result, run.slices);
  if (read === null) return null;

  const valuePath = valuePathRows(run.result, run.slices);
  // One-basis demo path (D14 / K7): the fixtures price in the schedule's
  // currency, and the shared formatter already prints its glyph with the
  // figure, so the KPI labels name only the period and the unit lives in the
  // figure. The component table keeps one glyph in its column header, the only
  // place a bare figure has no glyph of its own.
  const moneyUnit = currencyGlyph(read.currency);

  return (
    <Commentable name="Drawer.AnalysisRunFinancial">
      <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
        <SurfacePageSectionHeader
          title={read.title}
          actions={
            <HelpAnchor doc="advisory/analysis-run" section="estimated-ebitda-impact" />
          }
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Pill tone="default" title={read.formulaRev}>
              {read.basisLabel}
            </Pill>
            <span className="text-[10px] font-medium tracking-wide text-muted" data-run-estimate-label>{RUN_ESTIMATE_LABEL}</span>
          </div>
          {valuePath && valuePath.length > 0 ? (
            <button
              type="button"
              className="text-xs font-medium text-accent-700 hover:underline dark:text-accent-400"
              onClick={() => setOpen((current) => !current)}
            >
              {open ? 'Show fewer' : 'Show value path'}
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <KpiCard
            label="Per hour"
            value={read.perHourLabel}
            tone={read.tone}
          />
          <KpiCard
            label="Per day"
            value={read.perDayLabel}
            tone={read.tone}
          />
        </div>
        <Table wrap={false} className="text-xs">
          <THead>
            <TR>
              <TH className="px-0 py-1">Component</TH>
              <TH className="px-0 py-1 text-right">{`${moneyUnit} / h`}</TH>
            </TR>
          </THead>
          <TBody>
            {read.components.map((row) => (
              <TR key={row.node} title={`${row.formula} · ${row.unit}`}>
                <TD className="px-0 py-1">{row.label}</TD>
                <TD className="px-0 py-1 text-right tabular-nums">{row.sign}{row.valueLabel}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {open && valuePath && valuePath.length > 0 ? (
          <Table wrap={false} className="text-xs">
            <THead>
              <TR>
                <TH className="px-0 py-1">Node</TH>
                <TH className="px-0 py-1 text-right">Value</TH>
              </TR>
            </THead>
            <TBody>
              {valuePath.map((row) => (
                <TR key={row.nodeId} title={row.title}>
                  <TD className="px-0 py-1">{row.label}</TD>
                  <TD className="px-0 py-1 text-right tabular-nums">{row.valueLabel}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : null}
      </section>
    </Commentable>
  );
}

/* -------------------------------------------------------------------------
   The knowledge slice — the FMEA cause the run matched, the Control Plan row
   the signal should have held, and the TARP ladder that answers it.
   ------------------------------------------------------------------------- */

/**
 * The cause and the Control Plan row the envelope's `links` cite, and the
 * TARP ladder behind them.
 *
 * Two sections rather than one, because the reader asks two questions: what
 * the FMEA says this is and what the plan asked the circuit to hold; then who
 * does what, in what order, and when it escalates. The envelope carries ids
 * only, so every label here is the knowledge slice's own — a cause the slice
 * carries no label for is dropped rather than rendered as its id.
 *
 * The tier lines carry no status word: the envelope says nothing about how far
 * up the ladder this case has travelled, and the seeded case has no ladder of
 * its own, so the slice's three tiers are the whole of it.
 */
export function AnalysisRunKnowledge({ run }: { run: AnalysisRunState }) {
  if (run.status !== 'ready') return null;

  const read = knowledgeRead(run.result, run.slices);
  if (read === null) return null;
  if (read.causes.length === 0 && read.controlPlan.length === 0 && read.ladders.length === 0) {
    return null;
  }
  const causes = diagnosisOrderedCauses(read.causes, run.result.diagnosis);

  return (
    <>
      {read.causes.length > 0 || read.controlPlan.length > 0 ? (
        <Commentable name="Drawer.AnalysisRunKnowledge">
          <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
            <SurfacePageSectionHeader
              title="FMEA and Control Plan"
              actions={
                <HelpAnchor doc="advisory/analysis-run" section="fmea-and-control-plan" />
              }
            />
            <ul className="space-y-1 text-sm">
              {causes.map((row) => (
                <li key={row.causeId} title={row.title}>
                  {row.label}
                  {row.effectLabel ? (
                    <span className="text-muted">{' → '}{row.effectLabel}</span>
                  ) : null}
                </li>
              ))}
              {read.controlPlan.map((row) => (
                <li key={row.rowId} className="text-muted" title={row.title}>
                  {row.signalLabel} · {row.specLabel} · reliability {row.reliabilityLabel}
                </li>
              ))}
            </ul>
          </section>
        </Commentable>
      ) : null}
      {read.ladders.length > 0 ? (
        <Commentable name="Drawer.AnalysisRunLadder">
          <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
            <SurfacePageSectionHeader
              title="Escalation ladder"
              actions={<HelpAnchor doc="advisory/analysis-run" section="escalation-ladder" />}
            />
            {read.ladders.map((ladder) => (
              <ul key={ladder.ladderId} className="space-y-2 text-sm" title={ladder.title}>
                {ladder.tiers.map((tier) => (
                  <li key={tier.tier} className="text-muted">
                    {`${tier.tier} · ${tier.ownerLabel} · ${sentenceTarpInstruction(tier.instruction)} · ${tier.minutesLabel} → ${tier.escalateLabel}`}
                  </li>
                ))}
              </ul>
            ))}
          </section>
        </Commentable>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------
   Provenance — the revisions the whole answer was computed against.
   ------------------------------------------------------------------------- */

/**
 * The drawer's closing read: how many knowledge revisions this run was
 * computed against, with the ten pairs one hover down.
 *
 * The card carries a count and nothing else — the revision ids, the service
 * version and the computed instant are the traceability record and live on the
 * chip's tooltip. No sentence, no caveat, no source claim.
 */
export function AnalysisRunProvenance({ run }: { run: AnalysisRunState }) {
  if (run.status !== 'ready') return null;

  const prov = provenanceText(run.result);

  return (
    <Commentable name="Drawer.AnalysisRunProvenance">
      <section className="space-y-3 px-5 py-4">
        <SurfacePageSectionHeader
          title="Provenance"
          actions={<HelpAnchor doc="advisory/analysis-run" section="provenance" />}
        />
        <GlassCard className="px-4 py-3 text-sm text-muted">
          <ProvenanceChip text={prov.text} title={prov.title} />
        </GlassCard>
      </section>
    </Commentable>
  );
}
